import React, { useEffect, useCallback, useState, useRef } from 'react';
import styled from '@emotion/styled';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Button,
  StyledDiv,
  Typography,
  LoadingScreen,
  AlertDialog,
  Select,
  TextField,
  Spacing,
  CardContainer,
  Form,
  FormField,
  FormItem,
  FormControl,
  FormMessage,
  Icon,
  Dialog,
  JoyrideWalkthrough,
} from '@taleemabad/dsm/components';
import { useJoyride } from '@taleemabad/shared/hooks';
import { Checkbox } from '../../../atoms/checkbox/checkbox';
import { AlignUrduWrapper } from '../../../atoms/align-urdu/align-urdu';
import { BreakpointSizes, mq } from '@taleemabad/dsm/themes';

export interface ICoreLessonPlanGenerationRequest {
  subject: string;
  grade: number;
  topic?: string;
  pageNumberStart: number;
  pageNumberEnd: number;
  classStrength?: number;
  exercisePageNumber?: number;
  generateBilingual?: boolean;
  revisionLp: boolean;
  lpType?: string;
  isObjective?: boolean;
  isSubjective?: boolean;
  imageGenerationEnabled?: boolean;
  includeMedia?: boolean;
}

// Skill-based LP options per subject (matches LP Assistant VALID_LP_TYPES)
export const SKILL_LP_OPTIONS: Record<
  string,
  { content: string; value: string }[]
> = {
  Eng: [
    { content: 'Reading', value: 'reading' },
    {
      content: 'Comprehension (Word Meanings)',
      value: 'comprehension_word_meanings',
    },
    { content: 'Comprehension (Q&A)', value: 'comprehension_qa' },
    { content: 'Grammar', value: 'grammar' },
    { content: 'Creative Writing', value: 'creative_writing' },
    { content: 'Revision', value: 'revision' },
  ],
  Urdu: [
    { content: 'پڑھائی', value: 'reading' },
    { content: 'تفہیم : الفاظ / معانی , جملے', value: 'comprehension_word_meanings' },
    { content: 'تفہیم :سوالات و جوابات / خالی جگہ / غلط درست', value: 'comprehension_qa' },
    { content: 'گرائمر', value: 'grammar' },
    { content: 'تخلیقی لکھائی', value: 'creative_writing' },
    { content: 'دہرائی (Revision)', value: 'revision' },
  ],
  Math: [
    { content: 'Concrete', value: 'concrete' },
    { content: 'Pictorial & Abstract', value: 'pictorial_and_abstract' },
    { content: 'Word Problems', value: 'word_problems' },
    { content: 'Revision', value: 'revision' },
  ],
};

const REVISION_LP_TYPE_VALUE = 'revision';

export const getSkillLpOptions = (
  subject: string,
  isRevisionLpEnabled: boolean,
): { content: string; value: string }[] => {
  const options = SKILL_LP_OPTIONS[subject] ?? [];
  if (isRevisionLpEnabled) return options;
  return options.filter((o) => o.value !== REVISION_LP_TYPE_VALUE);
};

const SKILL_LP_DESCRIPTIONS: Record<string, Record<string, string>> = {
  default: {
    reading:
      'Develops reading fluency, pronunciation, and comprehension using FLN principles.',
    comprehension_word_meanings:
      'Helps students master new vocabulary and sentence construction through scaffolded activities.',
    comprehension_qa:
      'Guides students in extracting information and answering descriptive questions using the Scanning technique.',
    grammar:
      'Teaches a grammar rule per lesson using clear, direct instructions and zero-prep activities.',
    creative_writing:
      'Helps students brainstorm, organise, and write their own paragraphs or stories using interactive scaffolding.',
    concrete:
      'Hands-on lesson using physical objects so students build understanding before working with numbers.',
    pictorial_and_abstract:
      'Transitions students from drawings and diagrams (Pictorial) to mathematical symbols and algorithms (Abstract).',
    word_problems:
      'Teaches students to decode story problems using a structured "Math Detective" method.',
    revision:
      'Reviews previously taught content through objective and/or subjective questions.',
  },
  Urdu: {
    reading:
      'بنیادی پڑھائی کی مہارت، تلفظ اور سمجھ کو FLN اصولوں کے ذریعے بہتر بناتا ہے۔',
    comprehension_word_meanings:
      'مشکل الفاظ کے معانی سمجھنے اور انھیں جملوں میں استعمال کرنا سکھاتا ہے۔',
    comprehension_qa:
      'مختلف طریقوں کی مدد سے متن میں سے سوالات کے جوابات تلاش کرنا وغیرہ سکھاتا ہے۔',
    grammar:
      'گرائمر کے قوانین اور ان کے استعمال کے طریقے سکھاتا ہے۔',
    creative_writing:
      'طلباء کو سوچنے، ترتیب دینے اور اپنے پیراگراف یا کہانیاں لکھنے میں مدد کرتا ہے۔',
    revision:
      'پہلے پڑھائے گئے مواد کو معروضی یا تحریری سوالوں کے ذریعے دہراتا ہے۔',
  },
};

const getSkillLpDescription = (subject: string, lpType: string): string => {
  return (
    SKILL_LP_DESCRIPTIONS[subject]?.[lpType] ??
    SKILL_LP_DESCRIPTIONS['default'][lpType] ??
    ''
  );
};

const SKILL_LP_ICONS: Record<string, string> = {
  reading: 'menu_book',
  comprehension_word_meanings: 'translate',
  comprehension_qa: 'quiz',
  grammar: 'spellcheck',
  creative_writing: 'edit_note',
  concrete: 'category',
  pictorial_and_abstract: 'draw',
  word_problems: 'calculate',
  revision: 'replay',
};

export interface GenerateCoreLessonPlanProps {
  onGenerate: (request: ICoreLessonPlanGenerationRequest) => void;
  isGenerating?: boolean;
  showErrorPopup?: boolean;
  setShowErrorPopup: (value: boolean) => void;
  showNoInternetPopup?: boolean;
  setShowNoInternetPopup: (value: boolean) => void;
  showWeeklyLimitPopup?: boolean;
  setShowWeeklyLimitPopup: (value: boolean) => void;
  weeklyLPCount: number;
  weeklyLimitMsg?: string;
  gradeOptions?: { content: string; value: string }[];
  subjectOptions?: { content: string; value: string }[];
  getSubjectOptions?: (grade?: number) => { content: string; value: string }[];
  onViewLessonPlans?: () => void;
  isRevisionLpEnabled?: boolean;
  isSkillBasedLpEnabled?: boolean;
  weeklyLPLimit: number | null;
  initialGrade?: number;
  initialSubject?: string;
  onRevisionLpPreferencesChange?: (preferences: {
    isObjective: boolean;
    isSubjective: boolean;
  }) => void;
  isUserGenMediaEnabled?: boolean;
}

const getLessonPlanSchema = (
  isRevisionLpEnabled: boolean,
  isSkillBasedLpEnabled: boolean,
) =>
  z
    .object({
      grade: z.number({
        required_error: 'Please select a grade',
      }),
      subject: z
        .string({
          required_error: 'Please select a subject',
        })
        .min(1, 'Please select a subject'),
      topic: z.string().optional(),
      pageNumberStart: z
        .number({
          required_error: 'Start page is required',
          invalid_type_error: 'Start page must be a number',
        })
        .min(1, 'Start page must be at least 1'),
      pageNumberEnd: z
        .number({
          required_error: 'End page is required',
          invalid_type_error: 'End page must be a number',
        })
        .min(1, 'End page must be at least 1'),
      classStrength: z.number().optional(),
      exercisePageNumber: z.number().optional(),
      generateBilingual: z.boolean().optional(),
      revisionLp:
        isRevisionLpEnabled && !isSkillBasedLpEnabled
          ? z.boolean({
              required_error: 'Please select lesson plan type',
              invalid_type_error: 'Please select lesson plan type',
            })
          : z.boolean().optional(),
      lpType: z.string().optional(),
      isObjective: z.boolean().optional(),
      isSubjective: z.boolean().optional(),
      includeMedia: z.boolean().optional(),
    })
    .superRefine((data, ctx) => {
      if (
        typeof data.pageNumberStart === 'number' &&
        typeof data.pageNumberEnd === 'number' &&
        data.pageNumberEnd < data.pageNumberStart
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'End page must be greater than or equal to start page',
          path: ['pageNumberEnd'],
        });
      }
    })
    .superRefine((data, ctx) => {
      if (
        typeof data.pageNumberStart === 'number' &&
        typeof data.pageNumberEnd === 'number' &&
        data.pageNumberEnd - data.pageNumberStart + 1 > 10
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'You can select a maximum of 10 pages for one lesson plan.',
          path: ['pageNumberStart'],
        });
      }
    })
    .superRefine((data, ctx) => {
      // Skill-based mode: require lpType for non-Science subjects
      if (isSkillBasedLpEnabled && data.subject && data.subject !== 'Science') {
        if (!data.lpType) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Please select a lesson plan type',
            path: ['lpType'],
          });
        }
      }
      // Legacy revision mode: require revisionLp bool for Science or non-skill mode
      if (
        isRevisionLpEnabled &&
        (!isSkillBasedLpEnabled || data.subject === 'Science') &&
        data.revisionLp == null
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Please select lesson plan type',
          path: ['revisionLp'],
        });
      }
    })
    .superRefine((data, ctx) => {
      const isRevision = data.revisionLp === true || data.lpType === 'revision';
      if (isRevision && !data.isObjective && !data.isSubjective) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Please select at least one question type',
          path: ['isObjective'],
        });
      }
    });

type LessonPlanFormData = Omit<
  ICoreLessonPlanGenerationRequest,
  'revisionLp'
> & {
  revisionLp?: boolean;
  lpType?: string;
};

export const GenerateCoreLessonPlan: React.FC<GenerateCoreLessonPlanProps> = ({
  onGenerate,
  isGenerating = false,
  showErrorPopup = false,
  setShowErrorPopup,
  showNoInternetPopup = false,
  setShowNoInternetPopup,
  showWeeklyLimitPopup = false,
  weeklyLPCount,
  weeklyLimitMsg,
  setShowWeeklyLimitPopup,
  onViewLessonPlans,
  gradeOptions = [
    { content: 'Grade 1', value: '1' },
    { content: 'Grade 2', value: '2' },
    { content: 'Grade 3', value: '3' },
    { content: 'Grade 4', value: '4' },
    { content: 'Grade 5', value: '5' },
  ],
  subjectOptions = [
    { content: 'English', value: 'Eng' },
    { content: 'Urdu', value: 'Urdu' },
    { content: 'Mathematics', value: 'Math' },
  ],
  getSubjectOptions,
  isRevisionLpEnabled = false,
  isSkillBasedLpEnabled = false,
  weeklyLPLimit,
  initialGrade,
  initialSubject,
  onRevisionLpPreferencesChange,
  isUserGenMediaEnabled = false,
}) => {
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [showLpTypeModal, setShowLpTypeModal] = useState(false);

  const allWalkthroughSteps = [
    {
      target: '[data-cy="grade-subject-row"]',
      title: 'Grade & Subject',
      content: <><span>Select your class and subject. This helps us to build a lesson plan that fits your curriculum.</span><br /><br /><AlignUrduWrapper isUrdu={true}>جماعت اور مضمون کا درست انتخاب کریں تاکہ آپ کے لیے ایک بہترین منصوبہ بندی تیار کی جا سکے۔</AlignUrduWrapper></>,
      disableBeacon: true,
      placement: 'bottom' as const,
    },
    {
      target: '[data-cy="lesson-plan-type-select"]',
      title: 'Lesson Type',
      content: <><span>Choose your lesson type — Reading, Writing, or Comprehension. Each type generates a plan built specifically for that skill.</span><br /><br /><AlignUrduWrapper isUrdu={true}>اپنے سبق کی قسم کا انتخاب کریں ۔ہر قسم کے لیے اس مہارت کے مطابق الگ منصوبہ تیار ہوتا ہے۔</AlignUrduWrapper></>,
      disableBeacon: true,
      placement: 'bottom' as const,
      conditional: true,
    },
    {
      target: '[data-cy="pages-row"]',
      title: 'Book Pages',
      content: <><span>Enter the page numbers you will cover today (e.g., 24, 26). For a focused 40-minute lesson, 2 to 3 pages work best.</span><br /><br /><AlignUrduWrapper isUrdu={true}>پڑھائے جانے والے صفحات نمبر لکھیں جیسے ۲۴ اور ۲۵ ۔۴۰ منٹ کے بہترین سبق کے لیے ۲ یا ۳ صفحات کافی ہیں۔</AlignUrduWrapper></>,
      disableBeacon: true,
      placement: 'bottom' as const,
    },
    {
      target: '[data-cy="topic-input"]',
      title: 'Topic (Optional)',
      content: <><span>Want to focus on one specific skill from these pages? Write it here. Leave empty to cover the full content of the pages.</span><br /><br /><AlignUrduWrapper isUrdu={true}>اگر ان صفحات میں سے کسی ایک عنوان پرتوجہ دینی ہو تو یہاں لکھیں ورنہ جگہ خالی چھوڑ دیں ۔ اس طرح پورے صفحات کا احاطہ ہو جائے گا۔</AlignUrduWrapper></>,
      disableBeacon: true,
      placement: 'bottom' as const,
    },
    {
      target: '[data-cy="exercise-page-input"]',
      title: 'Exercise Page (Optional)',
      content: <><span>If there is a practice exercise linked to this lesson, add its page number here. It will be included in the independent practice section of your plan.</span><br /><br /><AlignUrduWrapper isUrdu={true}>اگر اس سبق سے جڑی ہوئی مشق ہے تو اس کا صفحہ نمبر یہاں لکھیں ۔یہ منصوبے کے آزادانہ مشق والے حصے میں شامل ہو جائے گا۔</AlignUrduWrapper></>,
      disableBeacon: true,
      placement: 'bottom' as const,
      conditional: true,
    },
    {
      target: '[data-cy="bilingual-checkbox"]',
      title: 'Urdu Translation',
      content: <><span>Tick this to receive your lesson plan in Urdu. Ideal for teachers who prefer to teach or share in Urdu.</span><br /><br /><AlignUrduWrapper isUrdu={true}>اردو میں سبق کا منصوبہ حاصل کرنے کے لیے یہاں کلک کریں ۔ ان اساتذہ کے لیے بہترین ہے جو اردو میں سبق پڑھانا چاہتے ہیں۔</AlignUrduWrapper></>,
      disableBeacon: true,
      placement: 'bottom' as const,
      conditional: true,
    },
    {
      target: '[data-cy="generate-button"]',
      title: 'Generate',
      content: <><span>All set! Tap to generate your complete 40-minute lesson plan — ready to teach in seconds.</span><br /><br /><AlignUrduWrapper isUrdu={true}>آپ کا منصوبہ تیار ہے ۔ اس بٹن کو دبائیں اور چند سیکنڈ میں ۴۰ منٹ کا بہترین منصوبہ تیار ہو جائے گا۔</AlignUrduWrapper></>,
      disableBeacon: true,
      placement: 'top' as const,
    },
  ];

  const {
    run,
    stepIndex,
    options: joyrideOptions,
    restartTour,
    nextStep,
    completeTour,
  } = useJoyride({
    localStorageKey: 'clp-generator-walkthrough',
    steps: allWalkthroughSteps,
  });

  const lessonPlanSchema = React.useMemo(
    () => getLessonPlanSchema(isRevisionLpEnabled, isSkillBasedLpEnabled),
    [isRevisionLpEnabled, isSkillBasedLpEnabled],
  );
  const form = useForm({
    resolver: zodResolver(lessonPlanSchema),
    defaultValues: {
      grade: initialGrade,
      subject: initialSubject,
      topic: undefined,
      pageNumberStart: undefined,
      pageNumberEnd: undefined,
      exercisePageNumber: undefined,
      generateBilingual: false,
      revisionLp: undefined,
      lpType: undefined,
      isObjective: false,
      isSubjective: false,
      includeMedia: false,
    },
  });

  const { control, watch, setValue, trigger } = form;

  const watchedGrade = watch('grade');
  const watchedSubject = watch('subject');
  const watchedPageStart = watch('pageNumberStart');
  const watchedPageEnd = watch('pageNumberEnd');
  const watchedRevisionLp = watch('revisionLp');
  const watchedLpType = watch('lpType');
  const watchedIsObjective = watch('isObjective');
  const watchedIsSubjective = watch('isSubjective');

  const isSkillSubject =
    isSkillBasedLpEnabled &&
    watchedSubject != null &&
    watchedSubject !== 'Science';
  const isRevisionSelected =
    watchedRevisionLp === true || watchedLpType === 'revision';

  const showBilingualOption = watchedSubject != null && watchedSubject !== 'Urdu';

  const activeWalkthroughSteps = React.useMemo(() => {
    return allWalkthroughSteps.filter((step) => {
      if (step.target === '[data-cy="lesson-plan-type-select"]') return isSkillSubject;
      if (step.target === '[data-cy="exercise-page-input"]') return watchedSubject === 'Math';
      if (step.target === '[data-cy="bilingual-checkbox"]') return showBilingualOption;
      return true;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSkillSubject, watchedSubject, showBilingualOption]);

  const currentSubjectOptions = getSubjectOptions
    ? getSubjectOptions(watchedGrade)
    : subjectOptions;

  const hasInitialValues = useRef(
    initialGrade !== undefined && initialSubject !== undefined,
  );

  // Reset subject if it's not available for the selected grade
  useEffect(() => {
    if (hasInitialValues.current) {
      hasInitialValues.current = false;
      return;
    }

    if (watchedGrade && watchedSubject && getSubjectOptions) {
      const availableSubjects = getSubjectOptions(watchedGrade);
      const isSubjectAvailable = availableSubjects.some(
        (option) => option.value === watchedSubject,
      );

      if (!isSubjectAvailable) {
        setValue('subject', '');
        form.trigger('subject');
      }
    }
  }, [watchedGrade, watchedSubject, getSubjectOptions, form, setValue]);

  useEffect(() => {
    if (
      typeof watchedPageStart === 'number' &&
      typeof watchedPageEnd === 'number'
    ) {
      trigger(['pageNumberStart', 'pageNumberEnd']);
    }
  }, [watchedPageStart, watchedPageEnd, trigger]);

  useEffect(() => {
    if (
      isRevisionSelected &&
      onRevisionLpPreferencesChange &&
      (watchedIsObjective || watchedIsSubjective)
    ) {
      onRevisionLpPreferencesChange({
        isObjective: watchedIsObjective ?? false,
        isSubjective: watchedIsSubjective ?? false,
      });
    }
  }, [
    isRevisionSelected,
    watchedIsObjective,
    watchedIsSubjective,
    onRevisionLpPreferencesChange,
  ]);

  // Reset lpType when subject changes in skill-based mode
  useEffect(() => {
    if (isSkillBasedLpEnabled) {
      setValue('lpType', undefined);
      setValue('isObjective', false);
      setValue('isSubjective', false);
    }
  }, [watchedSubject, isSkillBasedLpEnabled, setValue]);

  const handleInputFocus = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      setTimeout(() => {
        const element = event.target;
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest',
          });
        }
      }, 300);
    },
    [],
  );

  const onSubmit = (data: LessonPlanFormData) => {
    const request: ICoreLessonPlanGenerationRequest = {
      subject: data.subject,
      grade: data.grade,
      topic: data.topic?.trim(),
      pageNumberStart: data.pageNumberStart,
      pageNumberEnd: data.pageNumberEnd,
      exercisePageNumber: data.exercisePageNumber,
      generateBilingual: data.generateBilingual,
      revisionLp: data.revisionLp ?? false,
      lpType: data.lpType,
      isObjective: data.isObjective,
      isSubjective: data.isSubjective,
      includeMedia: data.includeMedia,
    };
    onGenerate(request);
  };

  const handleFormSubmit = useCallback(
    async (e?: React.MouseEvent) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }

      setShowValidationErrors(true);

      const isValid = await trigger();

      if (isValid) {
        const formData = form.getValues();
        onSubmit(formData);
      }
    },
    [trigger, form, onSubmit],
  );

  const handleFormElementSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  return (
    <>
      {isGenerating && (
        <LoadingScreen
          heading="Generating Lesson Plan"
          message="Please wait while AI creates your lesson plan..."
        />
      )}

      <Container>
        <AlertDialog
          title="Generation Error"
          description="Failed to generate lesson plan. Please try again."
          showPopup={showErrorPopup}
          onClosePopup={() => {
            setShowErrorPopup(false);
          }}
          data-cy="error-dialog"
        />

        <AlertDialog
          title="No Internet Connection"
          description="Seems like your internet is not connected. Connect and try again."
          showPopup={showNoInternetPopup}
          onClosePopup={() => {
            setShowNoInternetPopup(false);
          }}
          data-cy="no-internet-dialog"
        />

        <AlertDialog
          title="Weekly Limit Reached"
          description={
            weeklyLimitMsg ||
            'Your weekly lesson plan generation limit has been reached.'
          }
          showPopup={showWeeklyLimitPopup}
          onClosePopup={() => {
            setShowWeeklyLimitPopup(false);
          }}
          data-cy="weekly-limit-dialog"
        />

        <Spacing marginTop="xl" marginBottom="xl">
          <StyledDiv flexDirection="row" alignItem="center" justifyContent="space-between">
            <Typography variant="textBodyLarge">
              Create a comprehensive lesson plan for your class
            </Typography>
            <TipsButton
              type="button"
              onClick={() => restartTour()}
              data-cy="lp-tips-button"
            >
              <Icon name="lightbulb" color="--md-sys-color-primary" size="tiny" iconFill />
              <Typography variant="textBodyMedium" color="--md-sys-color-primary">
                Guide to Generate Good Lesson Plans
              </Typography>
            </TipsButton>
          </StyledDiv>
        </Spacing>

        <JoyrideWalkthrough
          run={run}
          steps={activeWalkthroughSteps}
          stepIndex={stepIndex}
          variant="styled"
          options={joyrideOptions}
          onNext={nextStep}
          onComplete={completeTour}
          onClose={completeTour}
          onSkip={completeTour}
        />
        {weeklyLPLimit !== null && (
          <Spacing marginBottom="lg">
            <StyledDiv flexDirection="row" alignItem="center" gap={8}>
              <Icon
                name="calendar_month"
                color="--md-sys-color-on-surface-variant"
              />
              <StyledDiv flexDirection="column">
                <Typography variant="textBodyMedium">
                  You have generated
                  <Typography variant="textBodyMediumSecondary30" inline>
                    {` ${weeklyLPCount} out of ${weeklyLPLimit} Lesson Plans `}
                  </Typography>
                  <Typography variant="textBodyMedium" inline>
                    this week.
                  </Typography>
                </Typography>
                <Typography variant="textBodySmall">
                  This limit is calculated based on your number of classes per
                  week and is reset every Monday.
                </Typography>
              </StyledDiv>
            </StyledDiv>
          </Spacing>
        )}
        <Spacing marginBottom="xl">
          <PaddedCardContainer
            variant={'outline'}
            width="calc(100% - 32px)"
            minHeight="auto"
          >
            <Form {...form}>
              <form onSubmit={handleFormElementSubmit} noValidate>
                <StyledDiv flexDirection="column">
                  <FormRow data-cy="grade-subject-row">
                    <FormField
                      control={control}
                      name="grade"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <Typography variant="textBodySmall" color="--md-sys-color-on-surface-variant">
                            Grade
                          </Typography>
                          <Spacing marginTop="sm" />
                          <FormControl>
                            <Select
                              key={`grade-${field.value}`}
                              variant="outlined"
                              defaultValue={field.value?.toString()}
                              width="100%"
                              onChange={(option) => {
                                field.onChange(
                                  Number.parseInt(option.value, 10),
                                );
                                form.trigger('grade');
                              }}
                              options={gradeOptions}
                              data-cy="grade-select"
                            />
                          </FormControl>
                          <Spacing marginTop="md" marginBottom="md">
                            <Typography
                              variant="textBodySmall"
                              color="--md-sys-color-on-surface-variant"
                            >
                              Select the grade for which you want to create the
                              lesson plan
                            </Typography>
                          </Spacing>
                          {showValidationErrors && fieldState.error && (
                            <FormMessage />
                          )}
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={control}
                      name="subject"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <Typography variant="textBodySmall" color="--md-sys-color-on-surface-variant">
                            Subject
                          </Typography>
                          <Spacing marginTop="sm" />
                          <FormControl>
                            <Select
                              key={`subject-${watchedGrade}-${field.value}`}
                              variant="outlined"
                              defaultValue={field.value}
                              width="100%"
                              onChange={(option) => {
                                field.onChange(option.value);
                                if (option.value !== 'Math') {
                                  setValue('exercisePageNumber', undefined);
                                }
                                form.trigger('subject');
                              }}
                              options={currentSubjectOptions}
                              data-cy="subject-select"
                            />
                          </FormControl>
                          <Spacing marginTop="md" marginBottom="md">
                            <Typography
                              variant="textBodySmall"
                              color="--md-sys-color-on-surface-variant"
                            >
                              Select the subject for which you want to create
                              the lesson plan
                            </Typography>
                          </Spacing>
                          {showValidationErrors && fieldState.error && (
                            <FormMessage />
                          )}
                        </FormItem>
                      )}
                    />
                  </FormRow>

                  <Spacing marginBottom="lg" />

                  {/* Skill-based LP type selector (non-Science subjects) */}
                  <FormRow>
                    {isSkillSubject && (
                      <FormField
                        control={control}
                        name="lpType"
                        render={({ field, fieldState }) => (
                          <FormItem>
                            <Typography variant="textBodySmall" color="--md-sys-color-on-surface-variant">
                              Lesson Plan Type
                            </Typography>
                            <Spacing marginTop="sm" />
                            <LpTypeButton
                              type="button"
                              onClick={() => setShowLpTypeModal(true)}
                              hasValue={!!field.value}
                              data-cy="lesson-plan-type-select"
                            >
                              <StyledDiv flexDirection="row" alignItem="center" justifyContent="space-between" width="100%">
                                <Typography
                                  variant="textBodyLarge"
                                  color={field.value ? '--md-sys-color-on-surface' : '--md-sys-color-on-surface-variant'}
                                >
                                  {field.value
                                    ? getSkillLpOptions(watchedSubject, isRevisionLpEnabled).find((o) => o.value === field.value)?.content ?? field.value
                                    : 'Select lesson plan type'}
                                </Typography>
                                <Icon name="expand_more" color="--md-sys-color-on-surface-variant" />
                              </StyledDiv>
                            </LpTypeButton>
                            {showValidationErrors && fieldState.error && <FormMessage />}
                            <Spacing marginTop="md" marginBottom="md">
                              <Typography variant="textBodySmall" color="--md-sys-color-on-surface-variant">
                                Choose the type of lesson you want to teach
                              </Typography>
                            </Spacing>

                            <Dialog
                              open={showLpTypeModal}
                              title="Lesson Plan Type"
                              onClose={() => setShowLpTypeModal(false)}
                            >
                              <Typography variant="textBodyMedium" color="--md-sys-color-on-surface-variant">
                                Each lesson plan type uses a dedicated teaching strategy — select the one that best fits your lesson.
                              </Typography>
                              <LpTypeCardList>
                                {getSkillLpOptions(watchedSubject, isRevisionLpEnabled).map((option) => (
                                  <LpTypeCard
                                    key={option.value}
                                    selected={field.value === option.value}
                                    onClick={() => {
                                      field.onChange(option.value);
                                      if (option.value !== 'revision') {
                                        setValue('isObjective', false);
                                        setValue('isSubjective', false);
                                      }
                                      form.trigger('lpType');
                                      setShowLpTypeModal(false);
                                    }}
                                  >
                                    <StyledDiv flexDirection="row" alignItem="center" gap={12}>
                                      <LpTypeIconWrapper selected={field.value === option.value}>
                                        <Icon
                                          name={SKILL_LP_ICONS[option.value] ?? 'school'}
                                          color={field.value === option.value ? '--md-sys-color-primary' : '--md-sys-color-on-surface-variant'}
                                          size="normal"
                                        />
                                      </LpTypeIconWrapper>
                                      <LpTypeCardContent>
                                        <AlignUrduWrapper isUrdu={watchedSubject === 'Urdu'}>
                                          <Typography variant="textBodyLarge">{option.content}</Typography>
                                          {getSkillLpDescription(watchedSubject, option.value) && (
                                            <Typography variant="textBodyMedium" color="--md-sys-color-on-surface-variant">
                                              {getSkillLpDescription(watchedSubject, option.value)}
                                            </Typography>
                                          )}
                                        </AlignUrduWrapper>
                                      </LpTypeCardContent>
                                      {field.value === option.value && (
                                        <Icon name="check_circle" color="--md-sys-color-primary" iconFill />
                                      )}
                                    </StyledDiv>
                                  </LpTypeCard>
                                ))}
                              </LpTypeCardList>
                            </Dialog>
                          </FormItem>
                        )}
                      />
                    )}

                    <StyledDiv flexDirection="column" data-cy="pages-row">
                      <Typography variant="textBodySmall" color="--md-sys-color-on-surface-variant">
                        Book Pages
                      </Typography>
                      <Spacing marginTop="sm" />
                      <StyledDiv flexDirection="row" alignItem="center" gap={12}>
                        <FormField
                          control={control}
                          name="pageNumberStart"
                          render={({ field, fieldState }) => (
                            <FlexFormItem>
                              <FormControl>
                                <TextField
                                  variant="outlined"
                                  placeholder="Start page"
                                  type="number"
                                  value={field.value?.toString() || ''}
                                  onInput={(e) => {
                                    const value = e.target.value ? Number.parseInt(e.target.value) : undefined;
                                    field.onChange(value);
                                    form.trigger('pageNumberStart');
                                  }}
                                  onFocus={handleInputFocus}
                                  data-cy="start-page-input"
                                />
                              </FormControl>
                              {showValidationErrors && fieldState.error && <FormMessage />}
                            </FlexFormItem>
                          )}
                        />
                        <StyledDiv>
                          <Typography variant="textBodyMedium">to</Typography>
                        </StyledDiv>
                        <FormField
                          control={control}
                          name="pageNumberEnd"
                          render={({ field, fieldState }) => (
                            <FlexFormItem>
                              <FormControl>
                                <TextField
                                  variant="outlined"
                                  placeholder="End page"
                                  type="number"
                                  value={field.value?.toString() || ''}
                                  onInput={(e) => {
                                    const value = e.target.value ? Number.parseInt(e.target.value) : undefined;
                                    field.onChange(value);
                                    form.trigger('pageNumberEnd');
                                  }}
                                  onFocus={handleInputFocus}
                                  data-cy="end-page-input"
                                />
                              </FormControl>
                              {showValidationErrors && fieldState.error && <FormMessage />}
                            </FlexFormItem>
                          )}
                        />
                      </StyledDiv>
                      <Spacing marginTop="md" marginBottom="md">
                        <Typography variant="textBodySmall" color="--md-sys-color-on-surface-variant">
                          Write the book pages you want to include.
                        </Typography>
                      </Spacing>
                    </StyledDiv>
                  </FormRow>

                  <Spacing marginBottom="lg" />

                  {isSkillSubject && isRevisionSelected && (
                    <>
                      <FormRow>
                        <FormItem>
                          <StyledDiv flexDirection="row" gap={24}>
                            <FormField
                              control={control}
                              name="isObjective"
                              render={({ field, fieldState }) => (
                                <FormItem>
                                  <FormControl>
                                    <StyledDiv alignItem="center" gap={4}>
                                      <Checkbox
                                        checked={field.value || false}
                                        onChange={(isChecked) => {
                                          field.onChange(isChecked);
                                          form.trigger(['isObjective', 'isSubjective']);
                                        }}
                                        data-cy="objective-checkbox"
                                      />
                                      <Typography variant="textBodyLarge">Objective</Typography>
                                    </StyledDiv>
                                  </FormControl>
                                  {showValidationErrors && fieldState.error && <FormMessage />}
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={control}
                              name="isSubjective"
                              render={({ field }) => (
                                <FormItem>
                                  <FormControl>
                                    <StyledDiv alignItem="center" gap={4}>
                                      <Checkbox
                                        checked={field.value || false}
                                        onChange={(isChecked) => {
                                          field.onChange(isChecked);
                                          form.trigger(['isObjective', 'isSubjective']);
                                        }}
                                        data-cy="subjective-checkbox"
                                      />
                                      <Typography variant="textBodyLarge">Subjective</Typography>
                                    </StyledDiv>
                                  </FormControl>
                                </FormItem>
                              )}
                            />
                          </StyledDiv>
                          <Spacing marginTop="md" marginBottom="md">
                            <Typography variant="textBodySmall" color="--md-sys-color-on-surface-variant">
                              Please select what type of questions you would like in the LP
                            </Typography>
                          </Spacing>
                        </FormItem>
                      </FormRow>
                      <Spacing marginBottom="lg" />
                    </>
                  )}

                  {/* Legacy revision LP selector (Science or when skill-based flag is off) */}
                  {isRevisionLpEnabled && !isSkillSubject && (
                    <>
                      <FormRow>
                        <FormField
                          control={control}
                          name="revisionLp"
                          render={({ field, fieldState }) => (
                            <FormItem>
                              <Typography variant="textBodySmall" color="--md-sys-color-on-surface-variant">
                              Lesson Plan Type
                            </Typography>
                          <Spacing marginTop="sm" />
                          <FormControl>
                                <Select
                                  variant="outlined"
                                  defaultValue={field.value?.toString()}
                                  width="100%"
                                  onChange={(option) => {
                                    const value = option.value === 'true';
                                    field.onChange(value);
                                    if (!value) {
                                      setValue('isObjective', false);
                                      setValue('isSubjective', false);
                                    }
                                    form.trigger('revisionLp');
                                  }}
                                  options={[
                                    { content: 'Regular LP', value: 'false' },
                                    { content: 'Revision LP', value: 'true' },
                                  ]}
                                  data-cy="lesson-plan-type-select"
                                />
                              </FormControl>
                              <Spacing marginTop="md" marginBottom="md">
                                <Typography
                                  variant="textBodySmall"
                                  color="--md-sys-color-on-surface-variant"
                                >
                                  Select whether you want a regular or revision
                                  lesson plan
                                </Typography>
                              </Spacing>
                              {showValidationErrors && fieldState.error && (
                                <FormMessage />
                              )}
                            </FormItem>
                          )}
                        />

                        {isRevisionSelected && (
                          <FormItem>
                            <StyledDiv flexDirection="row" gap={24}>
                              <FormField
                                control={control}
                                name="isObjective"
                                render={({ field, fieldState }) => (
                                  <FormItem>
                                    <FormControl>
                                      <StyledDiv alignItem="center" gap={4}>
                                        <Checkbox
                                          checked={field.value || false}
                                          onChange={(isChecked) => {
                                            field.onChange(isChecked);
                                            form.trigger([
                                              'isObjective',
                                              'isSubjective',
                                            ]);
                                          }}
                                          data-cy="objective-checkbox"
                                        />
                                        <Typography variant="textBodyLarge">
                                          Objective
                                        </Typography>
                                      </StyledDiv>
                                    </FormControl>
                                    {showValidationErrors &&
                                      fieldState.error && <FormMessage />}
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={control}
                                name="isSubjective"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormControl>
                                      <StyledDiv alignItem="center" gap={4}>
                                        <Checkbox
                                          checked={field.value || false}
                                          onChange={(isChecked) => {
                                            field.onChange(isChecked);
                                            form.trigger([
                                              'isObjective',
                                              'isSubjective',
                                            ]);
                                          }}
                                          data-cy="subjective-checkbox"
                                        />
                                        <Typography variant="textBodyLarge">
                                          Subjective
                                        </Typography>
                                      </StyledDiv>
                                    </FormControl>
                                  </FormItem>
                                )}
                              />
                            </StyledDiv>
                            <Spacing marginTop="md" marginBottom="md">
                              <Typography
                                variant="textBodySmall"
                                color="--md-sys-color-on-surface-variant"
                              >
                                Please select what type of questions you would
                                like in the LP
                              </Typography>
                            </Spacing>
                          </FormItem>
                        )}
                      </FormRow>

                      <Spacing marginBottom="lg" />
                    </>
                  )}

                  <Spacing marginBottom="lg" />

                  <FormRow>
                    <FormField
                      control={control}
                      name="topic"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <Typography variant="textBodySmall" color="--md-sys-color-on-surface-variant">
                            Topic Name (Optional)
                          </Typography>
                          <Spacing marginTop="sm" />
                          <FormControl>
                            <TextField
                              variant="outlined"
                              placeholder="Enter topic name"
                              value={field.value || ''}
                              onInput={(e) => {
                                field.onChange(e.target.value || undefined);
                                form.trigger('topic');
                              }}
                              onFocus={handleInputFocus}
                              data-cy="topic-input"
                            />
                          </FormControl>
                          <Spacing marginTop="md" marginBottom="md">
                            <Typography
                              variant="textBodySmall"
                              color="--md-sys-color-on-surface-variant"
                            >
                              Enter the topic of the lesson plan or leave blank
                              to auto-generate
                            </Typography>
                          </Spacing>
                          {showValidationErrors && fieldState.error && (
                            <FormMessage />
                          )}
                        </FormItem>
                      )}
                    />

                    <StyledDiv flexDirection="column">
                      {watchedSubject === 'Math' && (
                        <FormField
                          control={control}
                          name="exercisePageNumber"
                          render={({ field, fieldState }) => (
                            <FormItem>
                              <Typography variant="textBodySmall" color="--md-sys-color-on-surface-variant">
                                Exercise Page Number (Optional)
                              </Typography>
                              <Spacing marginTop="sm" />
                              <FormControl>
                                <TextField
                                  variant="outlined"
                                  placeholder="Enter exercise page number"
                                  type="number"
                                  value={field.value?.toString() || ''}
                                  onInput={(e) => {
                                    const value = e.target.value
                                      ? Number.parseInt(e.target.value)
                                      : undefined;
                                    field.onChange(value);
                                    form.trigger('exercisePageNumber');
                                  }}
                                  onFocus={handleInputFocus}
                                  data-cy="exercise-page-input"
                                />
                              </FormControl>
                              <Spacing marginTop="md" marginBottom="md">
                                <Typography
                                  variant="textBodySmall"
                                  color="--md-sys-color-on-surface-variant"
                                >
                                  Enter the exercise page number if available
                                </Typography>
                              </Spacing>
                              {showValidationErrors && fieldState.error && (
                                <FormMessage />
                              )}
                            </FormItem>
                          )}
                        />
                      )}
                    </StyledDiv>
                  </FormRow>

                  {(watchedSubject && watchedSubject !== 'Urdu') ||
                  isUserGenMediaEnabled ? (
                    <FormRow>
                      {watchedSubject && watchedSubject !== 'Urdu' && (
                        <FormField
                          control={control}
                          name="generateBilingual"
                          render={({ field, fieldState }) => (
                            <FormItem>
                              <FormControl>
                                <StyledDiv alignItem="center" gap={4}>
                                  <Checkbox
                                    checked={field.value || false}
                                    onChange={(isChecked) => {
                                      field.onChange(isChecked);
                                      form.trigger('generateBilingual');
                                    }}
                                    data-cy="bilingual-checkbox"
                                  />
                                  <Typography variant="textBodyLarge">
                                    Include Urdu Translation
                                  </Typography>
                                </StyledDiv>
                              </FormControl>
                              <Spacing marginTop="md" marginBottom="md">
                                <Typography
                                  variant="textBodySmall"
                                  color="--md-sys-color-on-surface-variant"
                                >
                                  Check this to include Urdu translation in the
                                  lesson plan
                                </Typography>
                              </Spacing>
                              {showValidationErrors && fieldState.error && (
                                <FormMessage />
                              )}
                            </FormItem>
                          )}
                        />
                      )}
                      {isUserGenMediaEnabled && (
                        <FormField
                          control={control}
                          name="includeMedia"
                          render={({ field, fieldState }) => (
                            <FormItem>
                              <FormControl>
                                <StyledDiv alignItem="center" gap={4}>
                                  <Checkbox
                                    checked={field.value || false}
                                    onChange={(isChecked) => {
                                      field.onChange(isChecked);
                                      form.trigger('includeMedia');
                                    }}
                                    data-cy="include-media-checkbox"
                                  />
                                  <Typography variant="textBodyLarge">
                                    Include Media
                                  </Typography>
                                </StyledDiv>
                              </FormControl>
                              <Spacing marginTop="md" marginBottom="md">
                                <Typography
                                  variant="textBodySmall"
                                  color="--md-sys-color-on-surface-variant"
                                >
                                  Check this to include images in the lesson
                                  plan
                                </Typography>
                              </Spacing>
                              {showValidationErrors && fieldState.error && (
                                <FormMessage />
                              )}
                            </FormItem>
                          )}
                        />
                      )}
                    </FormRow>
                  ) : null}

                  <Spacing marginBottom="xl" />
                </StyledDiv>
              </form>
            </Form>
          </PaddedCardContainer>
        </Spacing>

        <StyledButtonContainer>
          <Button
            type="button"
            variant="filled"
            disabled={isGenerating}
            onClick={handleFormSubmit}
            width="100%"
            data-cy="generate-button"
          >
            Generate Lesson Plan
          </Button>
          {onViewLessonPlans && (
            <Button
              variant="outlined"
              onClick={onViewLessonPlans}
              width="100%"
              data-cy="view-lesson-plans-button"
            >
              View Lesson Plans
            </Button>
          )}
        </StyledButtonContainer>
      </Container>
    </>
  );
};

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--rk-spacing-lg);

  ${mq(BreakpointSizes.xs)} {
    grid-template-columns: 1fr;
    gap: var(--rk-spacing-md);
  }
`;

const FlexFormItem = styled(FormItem)`
  flex: 1;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  padding: var(--rk-spacing-xl);
  position: relative;

  ${mq(BreakpointSizes.xs)} {
    padding-bottom: var(--rk-spacing-xxxxxxxxxxl);
  }
`;

const PaddedCardContainer = styled(CardContainer)`
  padding: var(--rk-spacing-xl);
  background-color: transparent;
  background: transparent;
`;

const StyledButtonContainer = styled.div`
  position: fixed;
  bottom: 50px;
  left: 50%;
  z-index: 100;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--rk-spacing-md);
  width: 300px;
  max-width: 90vw;

  ${mq(BreakpointSizes.xs)} {
    bottom: 75px;
    width: calc(100% - var(--rk-spacing-lg) * 2);
    left: var(--rk-spacing-lg);
    background-color: var(--md-sys-color-on-primary);
    padding: var(--rk-spacing-md);
  }
`;

const TipsButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: var(--md-sys-color-primary-container);
  border: none;
  border-radius: 20px;
  padding: 8px 14px;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  font-size: 14px;

  * {
    cursor: pointer;
  }
`;

const LpTypeButton = styled.button<{ hasValue: boolean }>`
  width: 100%;
  min-height: 56px;
  padding: var(--rk-spacing-md) var(--rk-spacing-lg);
  border-radius: 4px;
  border: 1px solid
    ${({ hasValue }) =>
      hasValue
        ? 'var(--md-sys-color-primary)'
        : 'var(--md-sys-color-outline)'};
  background: transparent;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.2s;
  display: flex;
  align-items: center;

  &:hover {
    border-color: var(--md-sys-color-on-surface);
  }
`;

const LpTypeCardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--rk-spacing-md);
  margin-top: var(--rk-spacing-md);
  max-height: 60vh;
  overflow-y: auto;
`;

const LpTypeCardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`;

const LpTypeIconWrapper = styled.div<{ selected: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${({ selected }) =>
    selected
      ? 'var(--md-sys-color-primary-container)'
      : 'var(--md-sys-color-surface-variant)'};
`;

const LpTypeCard = styled.div<{ selected: boolean }>`
  padding: var(--rk-spacing-md) var(--rk-spacing-lg);
  border-radius: 8px;
  border: 2px solid
    ${({ selected }) =>
      selected
        ? 'var(--md-sys-color-primary)'
        : 'var(--md-sys-color-outline-variant)'};
  background: ${({ selected }) =>
    selected
      ? 'var(--md-sys-color-primary-container)'
      : 'var(--md-sys-color-surface)'};
  cursor: pointer;
  transition:
    border-color 0.2s,
    background 0.2s;

  &:hover {
    border-color: var(--md-sys-color-primary);
    background: var(--md-sys-color-primary-container);
  }
`;
